import { type JSX, useState } from 'react';

import { ExternalIntegrationsApi } from 'Api/ExternalIntegrations/ExternalIntegrationsApi';
import { CountLinkButton } from 'Components/Buttons/CountLinkButton';
import { OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { Switch } from 'Components/Buttons/Switch';
import { Text } from 'Components/Text/Text';
import { ICON_ALERT_WARNING, ICON_HELP } from 'Config/Icons';
import { iso8601ToUsTimestampLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { IntegrationName, MetricName, MetricResponse } from 'Models/ExternalIntegrations';

import styles from './Metric.module.css';
import { Modal } from '../../ExternalIntegrations';

export interface MetricProps {
    externalIntegrationsApi: ExternalIntegrationsApi;
    displayAssociatedControls: (metric: MetricResponse) => void;
    displayMetricModal: (modal: Modal, metric: MetricResponse) => void;
    setSuccessFailureMessages: (newSuccessMessage: string | undefined, newFailureMessage: string | undefined) => void;
    integrationName: IntegrationName;
    metric: MetricResponse;
}

export const Metric = (props: MetricProps): JSX.Element => {
    const [enabled, setEnabled] = useState(props.metric.enabled ?? false);

    const toggleMetricHandler = async (event: React.FormEvent<HTMLInputElement>): Promise<void> => {
        props.setSuccessFailureMessages(undefined, undefined);

        const newEnabledValue = !enabled;
        setEnabled(newEnabledValue);
        try {
            await props.externalIntegrationsApi.toggleMetric(props.integrationName, props.metric.metric_name, { enabled: newEnabledValue });
        } catch (err) {
            props.setSuccessFailureMessages(undefined, err.message);
        }
    };

    const renderControlMapping = (): JSX.Element => {
        if (props.metric.metric_name === MetricName.THIRD_PARTY_MONITORING) {
            return <Text noStyles>N/A</Text>;
        } else {
            return <CountLinkButton count={props.metric.control_mapping?.length ?? 0} noun="Control" onClick={() => props.displayAssociatedControls(props.metric)} />;
        }
    };

    const getOverflowMenuProps = (): OverflowMenuProps => {
        const overflowMenuProps: OverflowMenuProps = {
            overflowItems: [
                {
                    text: 'Description',
                    onClickAction: () => props.displayMetricModal(Modal.MetricDescription, props.metric),
                    icon: ICON_HELP,
                },
            ],
            accessibilityTitle: `${props.metric.metric_name} Menu`,
        };

        if (props.metric.last_poll_success === false && props.metric.last_poll_error_message) {
            overflowMenuProps.overflowItems.push({
                text: 'Polling Error',
                onClickAction: () => props.displayMetricModal(Modal.MetricError, props.metric),
                icon: ICON_ALERT_WARNING,
            });
        }

        return overflowMenuProps;
    };

    return (
        <div className={styles.metricContainer}>
            <Switch checked={enabled} onChange={toggleMetricHandler} />
            <div className={styles.metricName}>
                <Text noStyles>{props.metric.metric_name}</Text>
            </div>
            <div className={styles.metricLastPoll}>
                <Text noStyles>Last Poll:</Text>
                {(() => {
                    switch (props.metric.last_poll_success) {
                        case true:
                            return (
                                <Text noStyles color="darkGreen">
                                    &nbsp;SUCCESS&nbsp;
                                </Text>
                            );
                        case false:
                            return (
                                <>
                                    <Text noStyles color="red">
                                        &nbsp;FAIL&nbsp;
                                    </Text>
                                </>
                            );
                        default:
                            return <Text noStyles>&nbsp;N/A&nbsp;</Text>;
                    }
                })()}
                {props.metric.last_poll_timestamp && <Text noStyles>{`(${iso8601ToUsTimestampLong(props.metric.last_poll_timestamp)})`}</Text>}
            </div>
            <div className={styles.metricControlMapping}>{renderControlMapping()}</div>
            <OverflowMenu {...getOverflowMenuProps()} />
        </div>
    );
};
